import React, { Component } from 'react'
import { toast } from 'react-toastify'
import { getString, getLocale } from '../../../locale'
import { onChannelCalibrationStatus, IChannelCalibrationStatus } from '../../../wsclient/handlers/channel-calibration-status.handler'
import {
    getChannelList,
    getChannelConfig,
    updateChannel,
    manualControl,
    allChannelsControl,
    calibrateChannel,
    ChannelInfo,
    ChannelConfigResponse,
    ChannelListResponse,
} from '../../../api/0x0005/channels'
import { getSyrupList, SyrupInfo, getDrinkList, DrinkInfo } from '../../../api/0x0005/drinks'

type Props = {
    clientId: string
}

type State = {
    channels: ChannelInfo[]
    syrups: SyrupInfo[]
    drinks: DrinkInfo[]
    loading: boolean
    selectedChannel?: number
    manualChannelState: number // 0 = off, 1 = on
    allChannelsState: { [channelNum: number]: number } // Track state for each channel in all control modal
    showEditModal: boolean
    showManualModal: boolean
    showAllControlModal: boolean
    showCalibrationModal: boolean
    calibrationState: {
        channelNum: number
        isRunning: boolean
        status: string
        resultDuration: number
        showResult: boolean
        showManualInput: boolean
        manualInputValue: string
    }
    editForm: {
        channelNum: number
        isActive: boolean
        mode: number // 0 = Solenoid, 1 = Relay
        pwmDuty: number
        syrupId: number
        syrupName: string
        channelName: string
        calibrationDurationMs: number
    }
}

export default class ChannelsTab extends Component<Props, State> {
    private unsubscribeCalibrationStatus?: () => void

    state: State = {
        channels: [],
        syrups: [],
        drinks: [],
        loading: true,
        selectedChannel: undefined,
        manualChannelState: 0,
        allChannelsState: {},
        showEditModal: false,
        showManualModal: false,
        showAllControlModal: false,
        showCalibrationModal: false,
        calibrationState: {
            channelNum: 0,
            isRunning: false,
            status: '',
            resultDuration: 0,
            showResult: false,
            showManualInput: false,
            manualInputValue: '',
        },
        editForm: {
            channelNum: 1,
            isActive: false,
            mode: 0,
            pwmDuty: 0,
            syrupId: 0,
            syrupName: '',
            channelName: '',
            calibrationDurationMs: 0,
        },
    }

    componentDidMount = async () => {
        await Promise.all([this.loadChannels(), this.loadSyrups(), this.loadDrinks()])
        window.addEventListener('beforeunload', this.handleBeforeUnload)

        // Subscribe to calibration status updates
        this.unsubscribeCalibrationStatus = onChannelCalibrationStatus(this.handleCalibrationStatus)
    }

    loadSyrups = async () => {
        try {
            const response = await getSyrupList(this.props.clientId)
            if (response.data.success === 1) {
                this.setState({ syrups: response.data.syrups })
            }
        } catch (error: any) {
            console.error('Failed to load syrups:', error)
        }
    }

    loadDrinks = async () => {
        try {
            const response = await getDrinkList(this.props.clientId)
            if (response.data.success === 1) {
                this.setState({ drinks: response.data.drinks })
            }
        } catch (error: any) {
            console.error('Failed to load drinks:', error)
        }
    }

    componentWillUnmount = () => {
        window.removeEventListener('beforeunload', this.handleBeforeUnload)
        if (this.unsubscribeCalibrationStatus) {
            this.unsubscribeCalibrationStatus()
        }
    }

    handleCalibrationStatus = (data: IChannelCalibrationStatus) => {
        const { clientId, channelNum, isCalibrating, finalDurationMs } = data

        // Only handle status for current client and current calibrating channel
        if (clientId !== this.props.clientId || channelNum !== this.state.calibrationState.channelNum) {
            return
        }

        if (isCalibrating === 0) {
            // Calibration completed
            this.setState({
                calibrationState: {
                    ...this.state.calibrationState,
                    isRunning: false,
                    showResult: true,
                    resultDuration: finalDurationMs,
                    status: getString('channels.calib_complete_msg'),
                },
            })
        }
    }

    handleBeforeUnload = (e: BeforeUnloadEvent) => {
        if (this.state.manualChannelState === 1) {
            e.preventDefault()
            e.returnValue = ''
        }
    }

    loadChannels = async () => {
        try {
            this.setState({ loading: true })
            const response = await getChannelList(this.props.clientId)
            if (response.data.success) {
                this.setState({ channels: response.data.channels })
            } else {
                toast.error(getString('channels.load_failed'))
            }
        } catch (err) {
            console.error(err)
            toast.error((err as Error).message)
        } finally {
            this.setState({ loading: false })
        }
    }

    handleChannelClick = async (channelNum: number) => {
        try {
            // Fetch full channel configuration
            const response = await getChannelConfig(this.props.clientId, channelNum)

            if (response.data.success) {
                const config = response.data
                this.setState({
                    selectedChannel: channelNum,
                    showEditModal: true,
                    editForm: {
                        channelNum,
                        isActive: config.isActive === 1,
                        mode: config.mode,
                        pwmDuty: config.pwmDuty,
                        syrupId: config.syrupId,
                        syrupName: config.syrupName || '',
                        channelName: config.channelName || '',
                        calibrationDurationMs: config.calibrationDurationMs || 0,
                    },
                })
            } else {
                toast.error(response.data.errorMsg || getString('channels.load_failed'))
            }
        } catch (err) {
            console.error(err)
            toast.error((err as Error).message)
        }
    }

    handleSaveChannel = async () => {
        try {
            const { editForm } = this.state
            const conflictingChannel = this.state.channels.find(
                (channel) =>
                    channel.channelNum !== editForm.channelNum &&
                    channel.syrupId !== 0 &&
                    channel.syrupId === editForm.syrupId
            )

            if (conflictingChannel) {
                toast.error(
                    getLocale() === 'he'
                        ? `הסירופ כבר משויך לערוץ ${conflictingChannel.channelNum}`
                        : `Syrup is already linked to channel ${conflictingChannel.channelNum}`
                )
                return
            }

            const response = await updateChannel(this.props.clientId, {
                channelNum: editForm.channelNum,
                isActive: editForm.isActive ? 1 : 0,
                mode: editForm.mode,
                pwmDuty: editForm.pwmDuty,
                syrupId: editForm.syrupId,
                calibrationDurationMs: editForm.calibrationDurationMs,
                channelName: editForm.channelName || '',
            })

            if (response.data.success) {
                toast.success(getString('channels.update_success', editForm.channelNum.toString()))
                this.setState({ showEditModal: false })
                await this.loadChannels()
            } else {
                toast.error(response.data.errorMsg || getString('channels.update_failed'))
            }
        } catch (err) {
            console.error(err)
            toast.error((err as Error).message)
        }
    }

    handleManualControl = async (channelNum: number, action: number) => {
        try {
            const channel = this.state.channels.find(c => c.channelNum === channelNum)
            const response = await manualControl(this.props.clientId, {
                channelNum,
                action,
                pwmValue: channel?.pwmDuty,
            })

            if (response.data.success) {
                this.setState({ manualChannelState: action })
                await this.loadChannels()
            } else {
                toast.error(response.data.errorMsg || getString('channels.control_failed'))
            }
        } catch (err) {
            console.error(err)
            toast.error((err as Error).message)
        }
    }

    handleAllChannelsControl = async (channelNum: number, action: number) => {
        try {
            const channel = this.state.channels.find(c => c.channelNum === channelNum)
            const response = await manualControl(this.props.clientId, {
                channelNum,
                action,
                pwmValue: channel?.pwmDuty,
            })

            if (response.data.success) {
                this.setState({
                    allChannelsState: {
                        ...this.state.allChannelsState,
                        [channelNum]: action,
                    },
                })
                await this.loadChannels()
            } else {
                toast.error(response.data.errorMsg || getString('channels.control_failed'))
            }
        } catch (err) {
            console.error(err)
            toast.error((err as Error).message)
        }
    }

    handleCloseAllControlModal = async () => {
        // Turn off any channels that are currently on
        const turnOffPromises = Object.entries(this.state.allChannelsState)
            .filter(([_, state]) => state === 1)
            .map(([channelNum]) =>
                manualControl(this.props.clientId, {
                    channelNum: parseInt(channelNum),
                    action: 0,
                    pwmValue: 0,
                })
            )

        await Promise.all(turnOffPromises)
        await this.loadChannels()
        this.setState({ showAllControlModal: false, allChannelsState: {} })
    }

    handleCalibrate = (channelNum: number) => {
        this.setState({
            showCalibrationModal: true,
            calibrationState: {
                channelNum,
                isRunning: false,
                status: getString('channels.calib_start_msg'),
                resultDuration: 0,
                showResult: false,
                showManualInput: false,
                manualInputValue: '',
            },
        })
    }

    handleCloseManualModal = async () => {
        const { selectedChannel, manualChannelState } = this.state

        // If channel is on, turn it off first
        if (manualChannelState === 1 && selectedChannel) {
            await this.handleManualControl(selectedChannel, 0)
        }

        this.setState({ showManualModal: false, selectedChannel: undefined, manualChannelState: 0 })
    }

    render() {
        const { channels, loading } = this.state

        if (loading) {
            return <div className="text-center p-10">{getString('channels.loading')}</div>
        }

        return (
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-5">
                <div className="flex justify-between items-center mb-5">
                    <h2 className="text-2xl font-bold dark:text-white">{getString('channels.title')}</h2>
                    <button
                        className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded"
                        onClick={() => this.setState({ showAllControlModal: true })}
                    >
                        {getString('channels.all_control')}
                    </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {channels.map(channel => this.renderChannelCard(channel))}
                </div>

                {this.state.showEditModal && this.renderEditModal()}
                {this.state.showManualModal && this.renderManualModal()}
                {this.state.showCalibrationModal && this.renderCalibrationModal()}
                {this.state.showAllControlModal && this.renderAllControlModal()}
            </div>
        )
    }

    renderChannelCard(channel: ChannelInfo) {
        const isActive = channel.isActive === 1
        const isCalibrating = channel.calibrationStatus === 1
        const modeName = channel.mode === 1 ? 'Relay' : 'Solenoid'
        const channelNumberLabel = `${getString('channels.channel')} #${channel.channelNum}`
        const channelDisplayName = channel.channelName?.trim() ? `${channel.channelName.trim()} (${channelNumberLabel})` : channelNumberLabel

        // Find the syrup for this channel
        const syrup = this.state.syrups.find(s => s.id === channel.syrupId)
        const isHebrew = require('../../../locale').getLocale() === 'he'
        const syrupName = syrup ? (isHebrew && syrup.nameHe ? syrup.nameHe : syrup.name) : getString('channels.syrup_none')

        // Find drinks that use this channel
        const channelIndex = channel.channelNum - 1
        const drinksUsingChannel = this.state.drinks.filter(drink =>
            drink.baseChannel === channelIndex || drink.flavourChannel === channelIndex
        )
        const drinkNames = drinksUsingChannel.map(drink =>
            isHebrew && drink.titleHe ? drink.titleHe : drink.title
        )

        return (
            <div
                key={channel.channelNum}
                className="border dark:border-gray-700 rounded-lg p-4 hover:shadow-lg transition-shadow cursor-pointer"
                onClick={() => this.handleChannelClick(channel.channelNum)}
            >
                <div className="flex justify-between items-center mb-2">
                    <div className="flex items-center gap-2">
                        <h3 className="text-lg font-bold dark:text-white">{channelDisplayName}</h3>
                        {isCalibrating && (
                            <span className="px-2 py-1 rounded text-xs font-semibold bg-yellow-100 text-yellow-800 animate-pulse">
                                {getString('channels.calibrating')}
                            </span>
                        )}
                    </div>
                    <span
                        className={`px-2 py-1 rounded text-xs font-semibold ${isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                            }`}
                    >
                        {isActive ? getString('channels.active') : getString('channels.inactive')}
                    </span>
                </div>

                <div className="space-y-1 text-sm dark:text-gray-300">
                    <div>
                        <span>{getString('channels.mode')}: </span>
                        <span className="font-semibold">{channel.mode === 1 ? getString('channels.mode_relay') : getString('channels.mode_solenoid')}</span>
                    </div>
                    {channel.mode === 1 && (
                        <div>
                            <span>{getString('channels.pwm')}: </span>
                            <span>{channel.pwmDuty}</span>
                        </div>
                    )}
                    <div>
                        <span>{getString('channels.syrup_name')}: </span>
                        <span className={syrup ? 'text-blue-600 dark:text-blue-400 font-semibold' : ''}>{syrupName}</span>
                    </div>
                    {drinksUsingChannel.length > 0 && (
                        <div>
                            <span>{getString('channels.used_in_drinks')}: </span>
                            <span className="text-green-600 dark:text-green-400 font-semibold">{drinkNames.join(', ')}</span>
                        </div>
                    )}
                    <div>
                        <span>{getString('channels.calibration')}: </span>
                        <span className={`font-semibold ${channel.calibrationDurationMs > 0 ? 'text-green-600' : 'text-gray-500'}`}>
                            {channel.calibrationDurationMs > 0 ? `${(channel.calibrationDurationMs / 1000).toFixed(1)}s` : getString('channels.not_calibrated')}
                        </span>
                    </div>
                </div>

                <div className="mt-3 flex gap-2">
                    <button
                        className="flex-1 bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded text-sm"
                        onClick={(e) => {
                            e.stopPropagation()
                            this.handleChannelClick(channel.channelNum)
                        }}
                    >
                        {getString('channels.btn_edit')}
                    </button>
                    <button
                        className="flex-1 bg-yellow-600 hover:bg-yellow-700 text-white px-3 py-1 rounded text-sm"
                        onClick={(e) => {
                            e.stopPropagation()
                            this.handleCalibrate(channel.channelNum)
                        }}
                    >
                        {getString('channels.btn_calibrate')}
                    </button>
                    <button
                        className="flex-1 bg-green-600 hover:bg-green-700 text-white px-3 py-1 rounded text-sm"
                        onClick={(e) => {
                            e.stopPropagation()
                            this.setState({ showManualModal: true, selectedChannel: channel.channelNum })
                        }}
                    >
                        {getString('channels.btn_manual')}
                    </button>
                </div>
            </div>
        )
    }

    renderEditModal() {
        const { editForm } = this.state

        return (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full">
                    <h3 className="text-xl font-bold mb-4 dark:text-white">
                        {getString('channels.edit_channel')} #{editForm.channelNum}
                    </h3>

                    <div className="space-y-4">
                        <div>
                            <label className="flex items-center space-x-2">
                                <input
                                    type="checkbox"
                                    checked={editForm.isActive}
                                    onChange={(e) => this.setState({
                                        editForm: { ...editForm, isActive: e.target.checked }
                                    })}
                                />
                                <span className="dark:text-white">{getString('channels.active')}</span>
                            </label>
                        </div>

                        <div>
                            <label className="block dark:text-white mb-2">{getString('channels.mode')}</label>
                            <select
                                value={editForm.mode}
                                onChange={(e) => this.setState({
                                    editForm: { ...editForm, mode: parseInt(e.target.value) }
                                })}
                                className="w-full border dark:border-gray-700 rounded px-3 py-2 dark:bg-gray-700 dark:text-white"
                            >
                                <option value={0}>{getString('channels.mode_solenoid')}</option>
                                <option value={1}>{getString('channels.mode_relay')}</option>
                            </select>
                        </div>

                        {editForm.mode === 1 && (
                            <div>
                                <label className="block dark:text-white mb-2">{getString('channels.pwm')} (%)</label>
                                <input
                                    type="number"
                                    min={0}
                                    max={100}
                                    value={editForm.pwmDuty}
                                    onChange={(e) => this.setState({
                                        editForm: { ...editForm, pwmDuty: parseInt(e.target.value) || 0 }
                                    })}
                                    className="w-full border dark:border-gray-700 rounded px-3 py-2 dark:bg-gray-700 dark:text-white"
                                />
                            </div>
                        )}

                        <div>
                            <label className="block dark:text-white mb-2">{getLocale() === 'he' ? 'שם ערוץ' : 'Channel name'}</label>
                            <input
                                type="text"
                                value={editForm.channelName}
                                onChange={(e) => this.setState({
                                    editForm: { ...editForm, channelName: e.target.value }
                                })}
                                className="w-full border dark:border-gray-700 rounded px-3 py-2 dark:bg-gray-700 dark:text-white"
                                maxLength={240}
                            />
                        </div>

                        <div>
                            <label className="block dark:text-white mb-2">{getString('channels.syrup_id')}</label>
                            <select
                                value={editForm.syrupId}
                                onChange={(e) => {
                                    const syrupId = parseInt(e.target.value) || 0
                                    const selectedSyrup = this.state.syrups.find(s => s.id === syrupId)
                                    const isHebrew = getLocale() === 'he'
                                    const displayName = selectedSyrup ? ((isHebrew && selectedSyrup.nameHe) ? selectedSyrup.nameHe : selectedSyrup.name) : ''
                                    this.setState({
                                        editForm: {
                                            ...editForm,
                                            syrupId,
                                            syrupName: displayName
                                        }
                                    })
                                }}
                                className="w-full border dark:border-gray-700 rounded px-3 py-2 dark:bg-gray-700 dark:text-white"
                            >
                                <option value="0">{getString('channels.syrup_none')}</option>
                                {this.state.syrups.map(syrup => {
                                    const isHebrew = getLocale() === 'he'
                                    const displayName = (isHebrew && syrup.nameHe) ? syrup.nameHe : syrup.name
                                    const linkedChannel = this.state.channels.find(
                                        (channel) =>
                                            channel.channelNum !== editForm.channelNum &&
                                            channel.syrupId === syrup.id
                                    )
                                    const disabled = !!linkedChannel
                                    return (
                                        <option key={syrup.id} value={syrup.id} disabled={disabled}>
                                            {syrup.id} - {displayName}
                                            {disabled
                                                ? ` (${getLocale() === 'he' ? `בשימוש בערוץ ${linkedChannel?.channelNum}` : `In use by ch ${linkedChannel?.channelNum}`})`
                                                : ''}
                                        </option>
                                    )
                                })}
                            </select>
                            {editForm.syrupName && (
                                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                                    {getString('channels.syrup_name')}: {editForm.syrupName}
                                </p>
                            )}
                        </div>

                        {editForm.calibrationDurationMs > 0 && (
                            <div className="p-3 bg-blue-50 dark:bg-blue-900 rounded">
                                <p className="text-sm dark:text-gray-300">
                                    {getString('channels.calibration_duration')}: {(editForm.calibrationDurationMs / 1000).toFixed(2)}s
                                </p>
                            </div>
                        )}
                    </div>

                    <div className="mt-6 flex gap-2">
                        <button
                            className="flex-1 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded"
                            onClick={this.handleSaveChannel}
                        >
                            {getString('channels.save')}
                        </button>
                        <button
                            className="flex-1 bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded"
                            onClick={() => this.setState({ showEditModal: false })}
                        >
                            {getString('channels.cancel')}
                        </button>
                    </div>
                </div>
            </div>
        )
    }

    renderManualModal() {
        const { selectedChannel, manualChannelState } = this.state
        if (!selectedChannel) return null

        return (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full">
                    <h3 className="text-xl font-bold mb-4 dark:text-white">
                        {getString('channels.manual_title')} - {getString('channels.channel')} #{selectedChannel}
                    </h3>

                    <p className="mb-4 dark:text-gray-300">
                        {getString('channels.manual_desc')}
                    </p>

                    {manualChannelState === 1 && (
                        <div className="mb-4 p-3 bg-green-100 dark:bg-green-900 border border-green-500 rounded">
                            <p className="text-green-800 dark:text-green-200 font-semibold text-center">
                                ⚠️ Channel is currently ON
                            </p>
                        </div>
                    )}

                    <div className="flex gap-2">
                        <button
                            className="flex-1 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded"
                            onClick={() => this.handleManualControl(selectedChannel, 1)}
                            disabled={manualChannelState === 1}
                        >
                            {getString('channels.turn_on')}
                        </button>
                        <button
                            className="flex-1 bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded"
                            onClick={() => this.handleManualControl(selectedChannel, 0)}
                            disabled={manualChannelState === 0}
                        >
                            {getString('channels.turn_off')}
                        </button>
                    </div>

                    <button
                        className="w-full mt-4 bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded"
                        onClick={this.handleCloseManualModal}
                    >
                        {getString('channels.cancel')}
                    </button>
                </div>
            </div>
        )
    }

    renderCalibrationModal() {
        const { calibrationState } = this.state
        const { channelNum, isRunning, status, showResult, resultDuration, showManualInput, manualInputValue } = calibrationState

        const handleStart = async () => {
            this.setState({
                calibrationState: {
                    ...calibrationState,
                    isRunning: true,
                    status: getString('channels.calib_running_msg'),
                },
            })

            try {
                const response = await calibrateChannel(this.props.clientId, { channelNum })

                if (!response.data.success) {
                    toast.error(response.data.errorMsg || getString('channels.calibrate_failed'))
                    this.setState({
                        calibrationState: {
                            ...calibrationState,
                            isRunning: false,
                            status: getString('channels.calib_start_msg'),
                        },
                    })
                }
                // If success, wait for ChannelCalibrationStatus from WebSocket
            } catch (err) {
                console.error(err)
                toast.error((err as Error).message)
                this.setState({
                    calibrationState: {
                        ...calibrationState,
                        isRunning: false,
                        status: getString('channels.calib_start_msg'),
                    },
                })
            }
        }

        const handleCancel = () => {
            this.setState({
                calibrationState: {
                    ...calibrationState,
                    isRunning: false,
                    status: getString('channels.calib_canceled'),
                },
            })
        }

        const handleSave = async () => {
            try {
                // Get current channel config
                const channel = this.state.channels.find(ch => ch.channelNum === channelNum)
                if (!channel) {
                    toast.error('Channel not found')
                    return
                }

                // Update channel with new calibration duration
                const response = await updateChannel(this.props.clientId, {
                    channelNum: channel.channelNum,
                    isActive: channel.isActive,
                    mode: channel.mode,
                    pwmDuty: channel.pwmDuty,
                    syrupId: channel.syrupId,
                    channelName: channel.channelName || '',
                    calibrationDurationMs: resultDuration,
                })

                if (response.data.success) {
                    toast.success(getString('channels.calibrate_success', channelNum.toString()))
                    await this.loadChannels()
                    this.setState({ showCalibrationModal: false })
                } else {
                    toast.error(response.data.errorMsg || 'Failed to save calibration')
                }
            } catch (err) {
                console.error(err)
                toast.error((err as Error).message)
            }
        }

        const handleRetry = () => {
            this.setState({
                calibrationState: {
                    ...calibrationState,
                    isRunning: false,
                    showResult: false,
                    status: getString('channels.calib_start_msg'),
                },
            })
        }

        const handleManualEntry = () => {
            this.setState({
                calibrationState: {
                    ...calibrationState,
                    showManualInput: !showManualInput,
                },
            })
        }

        const handleManualConfirm = () => {
            const duration = parseInt(manualInputValue)
            if (isNaN(duration) || duration <= 0) {
                toast.error('Please enter a valid duration')
                return
            }

            this.setState({
                calibrationState: {
                    ...calibrationState,
                    showResult: true,
                    resultDuration: duration,
                    status: getString('channels.calib_complete_msg'),
                    showManualInput: false,
                },
            })
        }

        const handleClose = () => {
            this.setState({ showCalibrationModal: false })
        }

        return (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full">
                    <h3 className="text-xl font-bold mb-4 dark:text-white">
                        {getString('channels.calibrate_confirm', channelNum.toString())}
                    </h3>

                    <p className="mb-6 text-center dark:text-gray-300">
                        {status}
                    </p>

                    {showResult && (
                        <div className="mb-4 p-4 bg-green-50 dark:bg-green-900 border border-green-500 rounded">
                            <p className="text-green-800 dark:text-green-200 font-bold text-center text-xl">
                                {getString('channels.calib_result', (resultDuration / 1000).toFixed(2))}
                            </p>
                        </div>
                    )}

                    {showManualInput && !showResult && (
                        <div className="mb-4">
                            <input
                                type="number"
                                placeholder={getString('channels.calib_manual_placeholder')}
                                value={manualInputValue}
                                onChange={(e) => this.setState({
                                    calibrationState: { ...calibrationState, manualInputValue: e.target.value }
                                })}
                                className="w-full border dark:border-gray-700 rounded px-3 py-2 dark:bg-gray-700 dark:text-white"
                            />
                        </div>
                    )}

                    <div className="space-y-2">
                        {!isRunning && !showResult && !showManualInput && (
                            <>
                                <button
                                    className="w-full bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded"
                                    onClick={handleStart}
                                >
                                    {getString('channels.calib_start')}
                                </button>
                                <button
                                    className="w-full bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded"
                                    onClick={handleManualEntry}
                                >
                                    {getString('channels.calib_manual')}
                                </button>
                            </>
                        )}

                        {showManualInput && !showResult && (
                            <button
                                className="w-full bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded"
                                onClick={handleManualConfirm}
                            >
                                {getString('channels.calib_confirm')}
                            </button>
                        )}

                        {isRunning && (
                            <button
                                className="w-full bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded"
                                onClick={handleCancel}
                            >
                                {getString('channels.calib_cancel_btn')}
                            </button>
                        )}

                        {showResult && (
                            <>
                                <button
                                    className="w-full bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded"
                                    onClick={handleSave}
                                >
                                    {getString('channels.calib_save')}
                                </button>
                                <button
                                    className="w-full bg-yellow-600 hover:bg-yellow-700 text-white px-4 py-2 rounded"
                                    onClick={handleRetry}
                                >
                                    {getString('channels.calib_retry')}
                                </button>
                            </>
                        )}

                        <button
                            className="w-full bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded"
                            onClick={handleClose}
                            disabled={isRunning}
                        >
                            {getString('channels.calib_close')}
                        </button>
                    </div>
                </div>
            </div>
        )
    }

    renderAllControlModal() {
        const { channels, allChannelsState } = this.state

        return (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-2xl w-full max-h-[80vh] overflow-y-auto">
                    <h3 className="text-xl font-bold mb-4 dark:text-white">{getString('channels.all_control_title')}</h3>

                    <p className="mb-4 dark:text-gray-300">
                        {getString('channels.all_control_desc')}
                    </p>

                    <div className="space-y-2 mb-4">
                        {channels.map(channel => {
                            const channelState = allChannelsState[channel.channelNum] || 0
                            return (
                                <div key={channel.channelNum} className="flex items-center justify-between p-3 border dark:border-gray-700 rounded">
                                    <div className="flex items-center gap-3">
                                        <span className="font-semibold dark:text-white">
                                            {getString('channels.channel')} #{channel.channelNum}
                                        </span>
                                        {channelState === 1 && (
                                            <span className="px-2 py-1 text-xs bg-green-100 text-green-800 rounded">
                                                ON
                                            </span>
                                        )}
                                    </div>
                                    <div className="flex gap-2">
                                        <button
                                            className="bg-green-600 hover:bg-green-700 text-white px-3 py-1 rounded text-sm"
                                            onClick={() => this.handleAllChannelsControl(channel.channelNum, 1)}
                                            disabled={channelState === 1}
                                        >
                                            {getString('channels.turn_on')}
                                        </button>
                                        <button
                                            className="bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded text-sm"
                                            onClick={() => this.handleAllChannelsControl(channel.channelNum, 0)}
                                            disabled={channelState === 0}
                                        >
                                            {getString('channels.turn_off')}
                                        </button>
                                    </div>
                                </div>
                            )
                        })}
                    </div>

                    <button
                        className="w-full bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded"
                        onClick={this.handleCloseAllControlModal}
                    >
                        {getString('channels.cancel')}
                    </button>
                </div>
            </div>
        )
    }
}
